import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import { generateCheckRecordPDF } from '../../utils/pdf';
import { IconArrowLeft, IconCheck, IconX, IconFileTypePdf } from '@tabler/icons-react';

export default function CheckDetail() {
  const { checkId } = useParams();
  const navigate = useNavigate();
  const { orgId, userProfile, org } = useAuth();
  const [check, setCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);

  const branchId = userProfile?.branchId;
  const resolvedOrgId = orgId || userProfile?.orgId;

  useEffect(() => {
    const load = async () => {
      if (!resolvedOrgId || !branchId) {
        setLoading(false);
        return;
      }
      try {
        const snap = await getDoc(
          doc(db, 'organisations', resolvedOrgId, 'branches', branchId, 'checkRecords', checkId)
        );
        if (snap.exists()) {
          setCheck({ id: snap.id, branchId, ...snap.data() });
        }
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [checkId, resolvedOrgId, branchId]);

  const formatTime = (ts) => {
    if (!ts) return '—';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) {
      return `Today ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleExportPDF = async () => {
    if (!check) return;
    setExportingPdf(true);
    try {
      generateCheckRecordPDF(check, org);
    } catch (err) {
      console.error(err);
    }
    setExportingPdf(false);
  };

  const failBehaviourColour = fb => fb === 'block' ? 'var(--color-danger)' : fb === 'notify' ? 'var(--color-warning)' : 'var(--color-text-tertiary)';

  if (loading) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading...</div>
    </Layout>
  );

  if (!check) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/checks')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Check history
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>Check record not found</div>
      </div>
    </Layout>
  );

  const sections = check.sections || [];
  const failedItems = sections.flatMap(s => (s.items || []).filter(i => i.result === 'fail'));
  const result = check.result || 'pass';

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/checks')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Check history
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>{check.templateName || 'Check record'}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--color-text-tertiary)', flexWrap: 'wrap' }}>
              {check.assetReg && (
                <span style={{ fontFamily: 'monospace', fontSize: 11, background: '#FAC775', color: '#412402', padding: '1px 6px', borderRadius: 3 }}>{check.assetReg}</span>
              )}
              {check.assetName && <span>{check.assetName}</span>}
              {check.submittedByName && <span>· {check.submittedByName}</span>}
              <span>· {formatTime(check.submittedAt)}</span>
              {result === 'fail' && (
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: 'var(--color-danger-light)', color: 'var(--color-danger)' }}>
                  {failedItems.length} failure{failedItems.length !== 1 ? 's' : ''}
                </span>
              )}
              {result === 'pass' && (
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: 'var(--color-success-light)', color: 'var(--color-success)' }}>
                  All passed
                </span>
              )}
            </div>
          </div>
          <button
            onClick={handleExportPDF}
            disabled={exportingPdf}
            style={{ background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '7px 14px', fontSize: 12, cursor: exportingPdf ? 'not-allowed' : 'pointer', opacity: exportingPdf ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 5 }}
          >
            <IconFileTypePdf size={14} stroke={1.5} /> {exportingPdf ? 'Generating...' : 'Export PDF'}
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Summary */}
            <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>
              <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Summary</div>
              {[
                { label: 'Submitted by', value: check.submittedByName },
                { label: 'Asset', value: `${check.assetName || '—'}${check.assetReg ? ` · ${check.assetReg}` : ''}` },
                { label: 'Branch', value: check.branchName || check.branchId },
                { label: 'Date & time', value: formatTime(check.submittedAt) },
                { label: 'Mileage', value: check.mileage ? `${Number(check.mileage).toLocaleString()} mi` : '—' },
                { label: 'Template', value: check.templateName },
                { label: 'Result', value: result.toUpperCase() },
              ].map(r => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                  <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{r.label}</span>
                  <span style={{ fontSize: 12, color: r.label === 'Result' ? (result === 'pass' ? 'var(--color-success)' : 'var(--color-danger)') : 'var(--color-text-primary)', fontWeight: r.label === 'Result' ? 600 : 400 }}>
                    {r.value || '—'}
                  </span>
                </div>
              ))}
            </div>

            {/* Audit trail */}
            <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>
              <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Audit trail</div>
              {(check.auditTrail || [
                { label: `Check submitted by ${check.submittedByName || '—'}`, time: formatTime(check.submittedAt), colour: 'var(--color-success)' },
                result === 'fail' ? { label: `${check.assetName || 'Asset'} blocked — failed check`, time: formatTime(check.submittedAt), colour: 'var(--color-danger)' } : null,
                { label: 'Push notification sent to branch manager', time: formatTime(check.submittedAt), colour: 'var(--color-warning)' },
              ].filter(Boolean)).map((a, i, arr) => (
                <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: i < arr.length - 1 ? '0.5px solid var(--color-border-light)' : 'none' }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: a.colour, marginTop: 5, flexShrink: 0 }} />
                  <div style={{ flex: 1, fontSize: 12, color: 'var(--color-text-secondary)' }}>{a.label}</div>
                  <div style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--color-text-tertiary)', flexShrink: 0 }}>{a.time}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Check items */}
          <div style={{ background: 'var(--color-bg-primary)', border: `0.5px solid ${result === 'fail' ? 'var(--color-danger)' : 'var(--color-border-light)'}`, borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Check items</div>

            {sections.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', padding: '24px 0', textAlign: 'center' }}>No check items recorded</div>
            ) : sections.map(section => (
              <div key={section.id || section.title}>
                <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em', padding: '8px 0 6px', borderBottom: '0.5px solid var(--color-border-light)', marginBottom: 2 }}>
                  {(section.title || section.name || '').toUpperCase()}
                </div>
                {(section.items || []).map((item, i) => (
                  <div key={item.id || i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: item.result === 'pass' ? 'var(--color-success-light)' : 'var(--color-danger-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                      {item.result === 'pass'
                        ? <IconCheck size={11} color="var(--color-success)" stroke={2} />
                        : <IconX size={11} color="var(--color-danger)" stroke={2} />
                      }
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: item.result === 'fail' ? 'var(--color-danger)' : 'var(--color-text-primary)', marginBottom: item.note ? 4 : 0 }}>
                        {item.label || item.name || '—'}
                      </div>
                      {item.note && (
                        <div style={{ fontSize: 12, color: 'var(--color-danger)', fontStyle: 'italic' }}>{item.note}</div>
                      )}
                    </div>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: failBehaviourColour(item.failBehaviour), flexShrink: 0, marginTop: 6 }} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}